import React, { useState, useEffect, useRef } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeRaw from 'rehype-raw';

const ChatMessageAssistant = ({ message, animateTyping = false, typingSpeed = 10000 }) => {
    const [displayedContent, setDisplayedContent] = useState('');
    const [isTyping, setIsTyping] = useState(false);
    const contentRef = useRef(message.content);
    const charIndexRef = useRef(0);
    const messageContentRef = useRef(null);
    const [finalContent, setFinalContent] = useState('');

    // Bei Änderungen der Nachricht oder Animation-Status
    useEffect(() => {
        contentRef.current = message.content;

        if (animateTyping && message.content) {
            // Animation aktiviert - starte mit leerem Inhalt
            setIsTyping(true);
            setDisplayedContent('');
            charIndexRef.current = 0;

            // Speichere vollständigen Inhalt für spätere Verwendung
            setFinalContent(message.content);
        } else {
            // Keine Animation - zeige sofort vollständigen Inhalt
            setDisplayedContent(message.content);
            setFinalContent(message.content);
            setIsTyping(false);
        }
    }, [message.content, animateTyping]);

    // Führe die Tippanimation durch
    useEffect(() => {
        if (!isTyping) return;

        const content = contentRef.current;
        if (!content) return;

        const totalChars = content.length;
        const animationDuration = 1500; // Millisekunden
        const charsPerBatch = Math.max(1, Math.ceil(totalChars / (animationDuration / 50))); // Zeichen pro Batch für flüssigere Animation
        let currentIndex = 0;

        const typingInterval = setInterval(() => {
            if (currentIndex >= totalChars) {
                // Animation beenden
                setIsTyping(false);
                clearInterval(typingInterval);

                // Dispatch ein Event, wenn die Animation abgeschlossen ist
                const event = new CustomEvent('typingComplete', {
                    detail: { messageId: message.id || Math.random().toString() }
                });
                document.dispatchEvent(event);
                return;
            }

            // Nächste Batch von Zeichen hinzufügen (für flüssigere Animation)
            const nextIndex = Math.min(currentIndex + charsPerBatch, totalChars);
            const nextChunk = content.substring(currentIndex, nextIndex);

            setDisplayedContent(prev => prev + nextChunk);
            currentIndex = nextIndex;

            // Dispatch ein Event für jedes Update, damit der Container scrollen kann
            const event = new CustomEvent('typingUpdate', {
                detail: { messageId: message.id || Math.random().toString() }
            });
            document.dispatchEvent(event);
        }, 50); // Häufigere Updates für flüssigere Animation

        return () => clearInterval(typingInterval);
    }, [isTyping, message.id]);

    // HTML-Inhalt sicher rendern
    const renderMarkdown = () => {
        return (
            <ReactMarkdown
                remarkPlugins={[remarkGfm]}
                rehypePlugins={[rehypeRaw]}
                components={{
                    // Benutzerdefinierte Komponenten für besseres Styling
                    h1: ({children}) => <h1 className="text-2xl font-bold mb-4">{children}</h1>,
                    h2: ({children}) => <h2 className="text-xl font-semibold mb-3">{children}</h2>,
                    h3: ({children}) => <h3 className="text-lg font-medium mb-2">{children}</h3>,
                    p: ({children}) => <p className="mb-3 leading-relaxed">{children}</p>,
                    ul: ({children}) => <ul className="list-disc mb-3 space-y-1 pl-4">{children}</ul>,
                    ol: ({children}) => <ol className="list-decimal mb-3 space-y-1 pl-4">{children}</ol>,
                    li: ({children}) => <li className="ml-0 pl-1">{children}</li>,
                    blockquote: ({children}) => (
                        <blockquote className="border-l-4 border-gray-300 pl-4 py-2 mb-3 bg-gray-50 italic">
                            {children}
                        </blockquote>
                    ),
                    code: ({inline, children}) => {
                        return inline ? (
                            <code className="bg-gray-100 px-1 py-0.5 rounded text-sm font-mono">{children}</code>
                        ) : (
                            <pre className="bg-gray-100 p-3 rounded mb-3 overflow-x-auto">
                                <code className="text-sm font-mono">{children}</code>
                            </pre>
                        );
                    },
                    strong: ({children}) => <strong className="font-semibold">{children}</strong>,
                    em: ({children}) => <em className="italic">{children}</em>,
                    hr: () => <hr className="my-6 border-gray-300" />,
                }}
            >
                {displayedContent}
            </ReactMarkdown>
        );
    };

    return (
        <div className="chat-message chat-message-assistant">
            <div
                className="message-content"
                ref={messageContentRef}
            >
                {renderMarkdown()}
            </div>
            {isTyping && <span className="typing-cursor ml-1">|</span>}
        </div>
    );
};

export default ChatMessageAssistant;
